import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import LogoutModel from "../LogoutModel/LogoutModel";
import {
  getAuthStateFromSession,
  getToken,
  getLoggedMethod,
  type AuthState,
  logout as sessionLogout,
} from "../../../features/session";
import "./gov-header.css";
import { handleRedirectBack } from "../../utils/navigationUtils";
import { logger } from "../../utils/logger.client";
import { useTranslation } from "react-i18next";
import LanguageToggle from "../LanguageToggle/LanguageToggle";

const SUPPORT_LINKS = [
  { href: "/contact", icon: "☎", label: "Contact Us" },
  { href: "/contact", icon: "✉", label: "Contact Us" },
  { href: "/faq", icon: "?", label: "FAQ" },
];

const ROUTES_WITHOUT_USER_DETAILS = new Set([
  "/login",
  "/information",
]);

const GovHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    provider: null,
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const headerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const updateHeaderHeight = () => {
      if (!headerRef.current) return;

      const headerHeight = headerRef.current.getBoundingClientRect().height;

      document.documentElement.style.setProperty(
        "--gov-header-height",
        `${headerHeight}px`
      );
    };

    updateHeaderHeight();
    window.addEventListener("resize", updateHeaderHeight);

    return () => {
      window.removeEventListener("resize", updateHeaderHeight);
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };

    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const s = await getAuthStateFromSession();
        if (!alive) return;

        if (s.isAuthenticated) {
          setAuthState(s);
          return;
        }

        const token = await getToken();
        const method = getLoggedMethod();

        if (!token || !method) {
          throw new Error("Missing token or authentication method");
        }

        setAuthState(prev => ({
          isAuthenticated: true,
          provider: prev.provider ?? method,
          user: prev.user ?? null,
        }));
      } catch {
        setAuthState({ isAuthenticated: false, user: null, provider: null });
      }
    })();

    return () => {
      alive = false;
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!authState.isAuthenticated || authState.user) return;

    (async () => {
      try {
        const s = await getAuthStateFromSession();

        if (s.user) {
          setAuthState(prev => ({
            ...prev,
            user: s.user,
            provider: s.provider ?? prev.provider,
          }));
        }
      } catch {}
    })();
  }, [authState.isAuthenticated, authState.user]);

  const handleLogoutClick = () => {
    setDrawerOpen(false);
    setShowLogoutModal(true);
  };

  const cancelLogout = () => setShowLogoutModal(false);

  const confirmLogout = async () => {
    setShowLogoutModal(false);
    setLoggingOut(true);

    try {
      await sessionLogout(navigate);
      await handleRedirectBack(navigate, true);

      setAuthState({
        isAuthenticated: false,
        user: null,
        provider: null,
      });
    } catch (e) {
      logger.error("[GovHeader] Logout failed:", e);
      setLoggingOut(false);
    }
  };

  const firstName = authState.user?.firstName || t("header.userFallback");
  const email = authState.user?.email;

  const normalizedPath =
  location.pathname !== "/"
    ? location.pathname.replace(/\/+$/, "")
    : "/";

  const showUserDetails = authState.isAuthenticated && !ROUTES_WITHOUT_USER_DETAILS.has(normalizedPath);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const path = location.pathname !== "/" ? location.pathname.replace(/\/+$/, "") : "/";
    const CLOSE_ON = new Set<string>(["/login", "/procedures", "/"]);

    if (CLOSE_ON.has(path)) {
      setLoggingOut(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!loggingOut) return;

    const id = window.setTimeout(() => setLoggingOut(false), 6000);

    return () => window.clearTimeout(id);
  }, [loggingOut]);

  return (
    <div>
      <header
        ref={headerRef}
        className={`header ${drawerOpen ? "header--drawer-open" : ""}`}
        id="BOX_GOV_HEADER"
      >
        <link
          href="https://partnerportal.mygovid.ie/Content/Design/assets/css/mygovid.css"
          rel="stylesheet"
        />

        <div className="logo" id="BOX_GOV_LOGO_CONTAINER">
          <img
            src="/logo.png"
            alt={t("header.logoAlt")}
            className="gov-logo"
            id="IMG_GOV_LOGO"
          />
          <span id="TXT_GOV_HEADER_TITLE" className="hidden md:inline-block">
            {t("header.title")}
          </span>
          <span id="TXT_GOV_HEADER_SHORT_TITLE" className="block md:hidden">
            {t("header.shortTitle")}
          </span>
        </div>

        <div className="header-right">
          <img
            src="/YE-logo_web_horisontal-panton_monochrom.png"
            alt={t("header.euFlagAlt")}
            className="eu-flag"
            id="IMG_EU_FLAG"
          />

              <span className="header-divider" aria-hidden="true" />

              <div className="user-dropdown">
                <button
                  className="user-button user-button--icon-left"
                  onClick={() => setDrawerOpen(v => !v)}
                  aria-haspopup="dialog"
                  aria-expanded={drawerOpen}
                  aria-label="Open menu"
                  id="BTN_USER_MENU"
                >
                  <span className="user-button__icon" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" role="img" focusable="false">
                      <path
                        fill="currentColor"
                        d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v1.2h19.2v-1.2c0-3.2-6.4-4.8-9.6-4.8z"
                      />
                    </svg>
                  </span>
                </button>
              </div>
        </div>
      </header>

      {drawerOpen && (
        <>
          <div
            className="drawer-backdrop"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />

          <aside className="user-drawer" id="BOX_USER_MENU" role="dialog" aria-modal="true">
            <div className="user-drawer__profile">
              {showUserDetails && (
                <div>
                  <div className="user-drawer__name">{firstName}</div>
                  {email && <div className="user-drawer__email">{email}</div>}
                </div>
              )}

              <button
                className="user-drawer__close"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close menu"
              >
                ×
              </button>
            </div>

            <label className="user-drawer__label">
              Preferred Language
            </label>

            <div className="user-drawer__language">
              <LanguageToggle />
            </div>

            {/*
            <h3 className="user-drawer__section">Support</h3>
            {SUPPORT_LINKS.map(({ href, icon, label }) => (
              <a key={`${href}-${icon}`} className="user-drawer__link" href={href}>
                <span className="support-icon">{icon}</span>
                {label}
              </a>
            ))}
              */}

            {showUserDetails && (
              <button
                className="user-drawer__logout"
                onClick={handleLogoutClick}
                id="BTN_LOGOUT"
              >
                {t("header.logout")}
              </button>
            )}
          </aside>
        </>
      )}

      {showLogoutModal && (
        <div
          className="overlay"
          id="BOX_HEADER_OVERLAY"
          role="dialog"
          aria-modal="true"
          aria-labelledby="TXT_HEADER"
        >
          <div className="popup">
            <h2 className="sub-header" id="TXT_HEADER">
              {t("header.logoutModal.title")}
            </h2>
            <p>{t("header.logoutModal.message")}</p>

            <div className="buttonContainer">
              <button className="cancelButton" id="BTN_HEADER_CANCEL" onClick={cancelLogout}>
                {t("header.logoutModal.cancel")}
              </button>

              <button className="consentButton" id="BTN_HEADER_CONFIRM" onClick={confirmLogout}>
                {t("header.logoutModal.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      <LogoutModel
        open={loggingOut}
        title={t("header.loggingOut.title")}
        subtitle={t("header.loggingOut.subtitle")}
      />
    </div>
  );
};

export default GovHeader;